import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper as SwiperComponent, SwiperSlide } from 'swiper/react';
import { type Swiper } from 'swiper/types';

import { useParams } from 'react-router-dom';
import { useArtifacts } from '../../../hooks/storeHooks/useArtifacts';
import { SuggestedMentorItem } from './SuggestedMentorItem';

export const SuggestedMentorsPanel: React.FC = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const { suggestedMentors, artifactPanelOpen } = useArtifacts();

  const sliderRef = useRef<Swiper>(null);
  const [showsPanel, setShowPanel] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const filteredMentors = useMemo(() => {
    if (!suggestedMentors?.length || !slug) return [];
    return suggestedMentors.filter((mentor) => mentor.slug !== slug);
  }, [suggestedMentors, slug]);

  const handleClosesPanel = () => {
    setShowPanel(false);
  };

  const onNextClick = () => sliderRef?.current?.slideNext();
  const onPrevClick = () => sliderRef?.current?.slidePrev();

  useEffect(() => {
    const hasFilteredMentors = filteredMentors.length > 0;
    setShowPanel(hasFilteredMentors);

    if (hasFilteredMentors) {
      const initialSlide = filteredMentors.length - 1;
      setCurrentSlide(initialSlide);

      if (sliderRef.current) {
        requestAnimationFrame(() => {
          sliderRef.current?.slideTo(initialSlide, 0);
        });
      }
    }
  }, [filteredMentors.length]);

  return showsPanel && !artifactPanelOpen ? (
    <div className="px-4 w-full fixed right-0 sm:right-24 bottom-10 sm:z-50 z-100 sm:w-[300px] min-[765px]:w-[320px] ">
      <div className="w-full rounded-2xl overflow-hidden tc-mv-blur p-2 md:p-4 md:pt-4 tc-mv-suggested-mentors-panel">
        <div className="flex items-center justify-end md:justify-between mb-2 text-white">
          <p className="font-semibold hidden md:block mb-1">
            {t('generation.suggestedCompanion')}
          </p>
        </div>
        <button
          className="absolute top-3 right-4 text-white z-10"
          onClick={handleClosesPanel}>
          <i className="fa fa-close text-xl" />
        </button>
        <SwiperComponent
          slidesPerView={1}
          onSlideChange={(swiper) => setCurrentSlide(swiper.activeIndex)}
          onSwiper={(swiper) => (sliderRef.current = swiper)}
          initialSlide={currentSlide}>
          {filteredMentors.map((mentor) => (
            <SwiperSlide key={mentor.mentorId}>
              <SuggestedMentorItem mentor={mentor} slug={slug} />
            </SwiperSlide>
          ))}
        </SwiperComponent>

        {currentSlide !== 0 && (
          <button
            className="absolute bottom-[4px] md:bottom-[14px] left-2 w-10 h-10 flex items-center justify-center z-10"
            onClick={onPrevClick}>
            <i className="fa fa-angle-left text-white" />
          </button>
        )}

        {currentSlide !== filteredMentors.length - 1 && (
          <button
            className="absolute bottom-[4px] md:bottom-[14px] right-2 w-10 h-10 flex items-center justify-center z-10"
            onClick={onNextClick}>
            <i className="fa fa-angle-right text-white" />
          </button>
        )}
      </div>
    </div>
  ) : null;
};
